import { useCallback, useEffect, useRef, useState } from 'react'
import { Square } from 'lucide-react'

const MAX_DURATION_S = 60

function _formatTime(seconds) {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0')
    const s = Math.floor(seconds % 60).toString().padStart(2, '0')
    return `${m}:${s}`
}

export default function AudioRecorder({ onComplete, onCancel }) {
    const [recording, setRecording] = useState(false)
    const [initiating, setInitiating] = useState(true) // waiting for getUserMedia
    const [elapsed, setElapsed] = useState(0)
    const [error, setError] = useState('')
    const [level, setLevel] = useState(0)
    const mediaRef = useRef(null)
    const streamRef = useRef(null)
    const chunksRef = useRef([])
    const startedAtRef = useRef(0)
    const tickerRef = useRef(null)
    const meterRef = useRef(null)

    const _start = async () => {
        setError('')
        setInitiating(true)
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
            setInitiating(false)
            setError('Запись голоса не поддерживается этим браузером.')
            return
        }
        let stream
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true },
            })
        } catch (e) {
            setInitiating(false)
            setError(e?.name === 'NotAllowedError' ? 'Разрешение на микрофон не выдано.' : `Не удалось включить микрофон: ${e?.message}`)
            return
        }
        setInitiating(false)
        streamRef.current = stream

        const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : MediaRecorder.isTypeSupported('audio/webm')
                ? 'audio/webm'
                : 'audio/ogg'
        const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 64_000 })
        mediaRef.current = rec
        chunksRef.current = []
        rec.ondataavailable = (e) => {
            if (e.data?.size > 0) chunksRef.current.push(e.data)
        }
        rec.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: mime.split(';')[0] })
            const duration = (Date.now() - startedAtRef.current) / 1000
            stream.getTracks().forEach((t) => t.stop())
            streamRef.current = null
            mediaRef.current = null
            setRecording(false)
            if (tickerRef.current) clearInterval(tickerRef.current)
            tickerRef.current = null
            if (meterRef.current) cancelAnimationFrame(meterRef.current)
            meterRef.current = null
            onComplete?.({ blob, durationSeconds: duration, mime: mime.split(';')[0] })
        }

        // Simple level meter via AudioContext + AnalyserNode.
        try {
            const AC = window.AudioContext || window.webkitAudioContext
            if (AC) {
                const ctx = new AC()
                const src = ctx.createMediaStreamSource(stream)
                const analyser = ctx.createAnalyser()
                analyser.fftSize = 256
                src.connect(analyser)
                const buf = new Uint8Array(analyser.frequencyBinCount)
                const tick = () => {
                    analyser.getByteFrequencyData(buf)
                    let sum = 0
                    for (const v of buf) sum += v
                    setLevel(Math.min(100, Math.round((sum / buf.length / 255) * 200)))
                    meterRef.current = requestAnimationFrame(tick)
                }
                tick()
            }
        } catch {
            /* meter is decorative — silent on failure */
        }

        startedAtRef.current = Date.now()
        rec.start(1000)
        setRecording(true)
        setElapsed(0)
        tickerRef.current = setInterval(() => {
            const s = (Date.now() - startedAtRef.current) / 1000
            setElapsed(s)
            if (s >= MAX_DURATION_S && rec.state !== 'inactive') rec.stop()
        }, 200)
    }

    const _stop = useCallback(() => {
        if (mediaRef.current && mediaRef.current.state !== 'inactive') {
            mediaRef.current.stop()
        }
    }, [])

    // Auto-start on mount: browser shows permission dialog immediately when
    // the user clicks the mic button (Telegram/WhatsApp UX — no extra click).
    useEffect(() => { void _start() }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => {
        if (tickerRef.current) clearInterval(tickerRef.current)
        if (meterRef.current) cancelAnimationFrame(meterRef.current)
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop())
    }, [])

    const remaining = Math.max(0, MAX_DURATION_S - elapsed)
    const warningWindow = remaining <= 10

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {error ? (
                <>
                    <div style={{ padding: 8, background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 6, fontSize: 12 }}>
                        {error}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={onCancel}>Отмена</button>
                        <button type="button" className="btn btn--primary btn--sm" onClick={_start} style={{ marginLeft: 'auto' }}>
                            Повторить
                        </button>
                    </div>
                </>
            ) : initiating ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                        Запрашиваю доступ к микрофону…
                    </span>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={onCancel}>Отмена</button>
                </div>
            ) : (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: 12, borderRadius: 10,
                    background: warningWindow ? 'var(--color-danger-bg)' : 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
                }}>
                    <span style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: 'var(--color-danger)',
                        animation: 'pulse 1.4s ease-in-out infinite',
                    }} />
                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {_formatTime(elapsed)} / 01:00
                    </span>
                    <div style={{
                        flex: 1, height: 6, borderRadius: 3,
                        background: 'rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: `${level}%`, height: '100%',
                            background: 'var(--color-accent)',
                            transition: 'width 80ms linear',
                        }} />
                    </div>
                    {warningWindow && (
                        <span style={{ color: 'var(--color-danger)', fontSize: 12 }}>
                            {Math.round(remaining)}s
                        </span>
                    )}
                    <button type="button" className="btn btn--primary btn--sm" onClick={_stop}>
                        <Square size={14} style={{ marginRight: 6 }} />
                        Стоп
                    </button>
                </div>
            )}
        </div>
    )
}
